import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Repeat, Check } from 'lucide-react';
import '../membership/MembershipAdmin.css';

/**
 * KitSwapSettingsCard — Club Settings → "Kit Swap" (B-45, v36).
 *
 * The one place the feature is switched on (OFF by default), plus the
 * club's handover wording ("Left behind the bar — ask for it by name" unless
 * the club says otherwise, e.g. "Clubhouse kit box"). Until it's on, Kit Swap
 * doesn't exist anywhere else in the app.
 *
 * Hydrates from the server on first data, then only while the form is clean
 * (the MembershipSettingsCard lesson — a background refetch must never wipe
 * unsaved edits, and a remount must always hydrate).
 */
export default function KitSwapSettingsCard({ clubSlug }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['kit-swap-settings', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit/admin/settings`).then(r => r.data),
  });

  const [enabled, setEnabled] = useState(false);
  const [handover, setHandover] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const hydratedRef = useRef(false);
  const dirtyRef = useRef(false);
  const serverSig = data ? JSON.stringify([!!data.enabled, data.handover_text]) : '';
  useEffect(() => {
    if (!data) return;
    if (hydratedRef.current && dirtyRef.current) return;
    setEnabled(!!data.enabled);
    setHandover(data.custom_handover ? data.handover_text : '');
    hydratedRef.current = true;
    // eslint-disable-next-line
  }, [serverSig]);

  const effectiveHandover = handover.trim() || data?.default_handover || '';
  const dirty = hydratedRef.current && data && (
    enabled !== !!data.enabled || effectiveHandover !== data.handover_text
  );
  dirtyRef.current = !!dirty;

  const save = async () => {
    setError(''); setSaved(false);
    if (handover.trim().length > 120) { setError('Keep the handover wording under 120 characters.'); return; }
    if (!enabled && data.enabled && (data.live_listings || 0) > 0
      && !window.confirm(`Switching Kit Swap off hides it for everyone, including ${data.live_listings} live listing${data.live_listings === 1 ? '' : 's'}. They come back if you switch it on again. Continue?`)) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/clubs/${clubSlug}/kit/admin/settings`, { enabled, handover_text: handover.trim() });
      qc.setQueryData(['kit-swap-settings', clubSlug], res.data);
      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
      qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });
      setHandover(res.data.custom_handover ? res.data.handover_text : '');
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save Kit Swap settings.');
    } finally { setSaving(false); }
  };

  return (
    <div className="card settings-card" style={{ marginTop: 16 }} data-tour="settings-kit-swap">
      <h3 style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><Repeat size={18} /> Kit Swap</h3>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Members pass on good outgrown kit for free. Someone lists it with a photo; another member taps
        <strong> I'd like this</strong>; the kit is left for them to collect. No money changes hands and there are
        no prices anywhere. It sits in <strong>Shop</strong> as a second tab, next to Kit Shop. Adults only — you
        can remove any listing, with a reason the person who listed it will see.
      </p>

      {isLoading && <div className="page-loading" style={{ minHeight: 80 }}><div className="spinner" /></div>}
      {data && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
            <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); setSaved(false); }} style={{ width: 18, height: 18, cursor: 'pointer' }} />
            Kit Swap is {enabled ? 'on' : 'off'} for this club
          </label>
          {data.enabled && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 0 28px' }}>
              {data.live_listings} live listing{data.live_listings === 1 ? '' : 's'} right now.
            </p>
          )}

          <div className="form-group" style={{ marginTop: 18 }}>
            <label className="label" htmlFor="kit-handover">Where is the kit left?</label>
            <input id="kit-handover" className="input" maxLength={120} value={handover}
              placeholder={data.default_handover}
              onChange={e => { setHandover(e.target.value); setSaved(false); }} />
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              Shown to everyone who lists or claims kit. Leave blank for “{data.default_handover}”.
            </p>
          </div>

          {error && <div className="madm-note is-error">{error}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save Kit Swap settings'}
            </button>
            {saved && !dirty && (
              <span style={{ fontSize: 13, color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} /> Saved
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
